"use client";

import { type OrgItem, OrgSwitcherModal } from "@atlas/ui";
import { useOrganization } from "@clerk/nextjs";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAdminOrganizations } from "@/hooks/admin/useAdminOrganizations";
import { getOrgOverride, setOrgOverride } from "@/lib/api-client";

const ORG_TYPE_LABELS: Record<string, string> = {
  academic_medical_center: "Academic Medical Center",
  community_health_system: "Community Health System",
  critical_access_hospital: "Critical Access Hospital",
  physician_group: "Physician Group",
  specialty_hospital: "Specialty Hospital",
  safety_net_hospital: "Safety Net Hospital",
};

/**
 * Admin-only floating org switcher pinned to the bottom-left of every page,
 * just inside the left nav and above the opportunity toolbar. Bottom-right is
 * avoided because the report right rail's "Revise report" apply button lives
 * there. Sits above route content so it remains usable on error boundaries and
 * other surfaces that don't render the dashboard sidebar.
 */
export function FloatingOrgSwitcher() {
  const { organization: activeOrg } = useOrganization();
  const isSuperAdmin = activeOrg?.slug === "elion";
  const { data } = useAdminOrganizations({ enabled: isSuperAdmin });
  const queryClient = useQueryClient();

  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(() =>
    getOrgOverride(),
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!selectedOrgId && activeOrg?.id) {
      setSelectedOrgId(activeOrg.id);
      setOrgOverride(activeOrg.id);
    }
  }, [selectedOrgId, activeOrg?.id]);

  const adminOrgs = data?.organizations ?? [];

  if (!mounted || !isSuperAdmin || adminOrgs.length === 0) return null;

  const orgs: OrgItem[] = adminOrgs.map((o) => ({
    id: o.id,
    name: o.name,
    shortName: o.slug ?? undefined,
    type: o.orgType && ORG_TYPE_LABELS[o.orgType],
    region: [o.state, o.region].filter(Boolean).join(" ") || undefined,
  }));

  const selectedOrg = orgs.find((o) => o.id === selectedOrgId);
  const label = selectedOrg?.name ?? activeOrg?.name ?? "Select org";

  function handleSelect(orgId: string) {
    setSelectedOrgId(orgId);
    setOrgOverride(orgId);
    void queryClient.invalidateQueries();
  }

  return createPortal(
    <>
      <button
        type="button"
        className={[
          "fixed bottom-14 left-64 z-[60]",
          "inline-flex items-center gap-2",
          "px-3 h-8",
          "border rounded-[3px]",
          "text-(--text-xs) font-medium",
          "shadow-(--shadow-sm)",
          "transition-[background,color,border-color] duration-120",
          "cursor-pointer",
          "bg-(--bg-white) text-(--fg-secondary) border-(--border-default)",
          "hover:bg-(--bg-canvas) hover:text-(--fg-primary) hover:border-(--brand-navy)",
        ].join(" ")}
        onClick={() => setModalOpen(true)}
        title="Switch organization"
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden={true}
        >
          <path d="M3 21h18" />
          <path d="M5 21V7l7-4 7 4v14" />
          <path d="M9 9h2M9 13h2M9 17h2M13 9h2M13 13h2M13 17h2" />
        </svg>
        {label}
      </button>

      {modalOpen && (
        <OrgSwitcherModal
          orgs={orgs}
          activeOrgId={selectedOrgId}
          onSelect={handleSelect}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>,
    document.body,
  );
}
